import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { RefreshCw, ExternalLink, Sparkles, ChevronDown, ChevronUp, Maximize2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString();
}
function fmtShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function hostOf(u) {
  try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; }
}

function EntryRow({ entry, index, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border-l-2 border-blue-500/40 pl-3" data-testid={`mh-entry-${index}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left group"
        data-testid={`mh-entry-toggle-${index}`}
      >
        <span className="text-xs font-mono uppercase tracking-wider text-zinc-400 group-hover:text-zinc-100">
          {fmtWhen(entry.generated_at)}
          {entry.is_incremental && (
            <span className="ml-2 text-[9px] text-blue-400 border border-blue-500/30 bg-blue-500/10 rounded-sm px-1 py-0.5">Δ Update</span>
          )}
        </span>
        <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase text-zinc-600">
          {(entry.citations?.length || 0) > 0 && <span>{entry.citations.length} sources</span>}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="mt-2 space-y-3">
          <div className="text-sm leading-relaxed text-zinc-200 [&_p]:my-2 [&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-1 [&_strong]:text-zinc-50 [&_a]:text-blue-400 [&_a:hover]:text-blue-300 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:mt-3 [&_h2]:mt-3 [&_h3]:mt-3">
            <ReactMarkdown
              components={{
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                ),
              }}
            >
              {entry.content || ""}
            </ReactMarkdown>
          </div>

          {(entry.citations?.length || 0) > 0 && (
            <div className="pt-2 border-t border-zinc-900">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Sources</div>
              <ul className="space-y-1">
                {entry.citations.map((c, i) => (
                  <li key={i} className="text-sm">
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`mh-citation-${index}-${i}`}
                      className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"
                    >
                      <span className="text-zinc-500 font-mono text-xs">[{i + 1}]</span>
                      <span className="truncate max-w-[440px]">{c.title || hostOf(c.url)}</span>
                      <ExternalLink className="w-3 h-3 opacity-60" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {entry.model && (
            <div className="text-[10px] font-mono uppercase text-zinc-600" title={entry.model}>
              Model: {entry.model}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MarketHistory({ trade, onUpdated }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [focusEntryId, setFocusEntryId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const pollRef = useRef(null);

  const status = trade?.market_history_status;
  const legacy = trade?.market_history;
  const entriesRaw = trade?.market_history_entries;

  const entries = useMemo(() => {
    if (Array.isArray(entriesRaw) && entriesRaw.length > 0) {
      return [...entriesRaw].sort((a, b) => (b.generated_at || "").localeCompare(a.generated_at || ""));
    }
    if (legacy?.content) return [{ id: "legacy", ...legacy }];
    return [];
  }, [entriesRaw, legacy]);

  const isPending = status === "pending";
  const isError = status === "error";
  const hasAny = entries.length > 0;

  // Poll while pending.
  useEffect(() => {
    if (!isPending || !trade?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/trades/${trade.id}`);
        if (cancelled) return;
        onUpdated?.(data);
        if (data.market_history_status !== "pending") return;
        pollRef.current = setTimeout(tick, 4000);
      } catch {
        pollRef.current = setTimeout(tick, 6000);
      }
    };
    pollRef.current = setTimeout(tick, 4000);
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [isPending, trade?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const regenerate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/trades/${trade.id}/market-history/regenerate`);
      const { data } = await api.get(`/trades/${trade.id}`);
      onUpdated?.(data);
      toast.success("Analyzing market history…");
    } catch {
      toast.error("Could not start market history regeneration");
    } finally {
      setBusy(false);
    }
  };

  const openEntry = (entryId) => {
    setFocusEntryId(entryId);
    setDialogOpen(true);
  };

  /** Open the popup already focused on the most recent entry — used by the
   * new header Maximize icon so a single click surfaces the latest summary. */
  const openLatest = () => {
    if (!entries.length) return;
    // `entries` is already sorted desc by generated_at.
    const latest = entries[0];
    openEntry(latest.id || latest.generated_at);
  };

  // Sort entries so the focused one appears at top when the popup opens.
  const orderedEntries = useMemo(() => {
    if (!focusEntryId) return entries;
    const idx = entries.findIndex((e) => (e.id || e.generated_at) === focusEntryId);
    if (idx < 0) return entries;
    return [entries[idx], ...entries.filter((_, i) => i !== idx)];
  }, [entries, focusEntryId]);

  return (
    <>
      {/* Inline widget in the sidebar: header + list of dated updates */}
      <div className="rounded-sm border border-zinc-900 bg-[#0a0a0a]" data-testid="market-history">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-900">
          <button
            onClick={() => setCollapsed((v) => !v)}
            data-testid="mh-collapse-toggle"
            className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-wider text-zinc-400 hover:text-zinc-200"
          >
            <Sparkles className="w-4 h-4 text-blue-400" /> Market History
            {hasAny && <span className="text-zinc-600">· {entries.length}</span>}
            {collapsed ? <ChevronDown className="w-3.5 h-3.5 ml-1" /> : <ChevronUp className="w-3.5 h-3.5 ml-1" />}
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={regenerate}
              disabled={busy || isPending}
              data-testid="mh-regenerate-inline"
              title={isPending ? "Analyzing…" : "Regenerate — ask Gemini for the latest catalysts"}
              className="p-1 text-zinc-500 hover:text-blue-400 disabled:opacity-30"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${busy || isPending ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={openLatest}
              disabled={!hasAny}
              data-testid="mh-expand-btn"
              title="Open the most recent update"
              className="p-1 text-zinc-500 hover:text-blue-400 disabled:opacity-30"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {!collapsed && (
        <>

        {/* Body */}
        {!hasAny && !isPending && !isError && (
          <button
            onClick={regenerate}
            className="w-full text-left px-3 py-3 text-sm font-mono uppercase text-zinc-500 hover:bg-zinc-900/40"
            data-testid="mh-generate-first"
          >
            No updates yet — click to generate the first one
          </button>
        )}

        {isPending && !hasAny && (
          <div className="px-3 py-3 text-sm font-mono uppercase text-zinc-500 inline-flex items-center gap-2">
            <RefreshCw className="w-3 h-3 animate-spin text-blue-400" /> Analyzing {trade?.ticker}…
          </div>
        )}

        {isError && !hasAny && (
          <div className="px-3 py-3 text-sm text-rose-400">
            Analysis failed. <button onClick={regenerate} className="underline">try again</button>
          </div>
        )}

        {hasAny && (
          <ul className="divide-y divide-zinc-900" data-testid="mh-list">
            {entries.map((e, i) => {
              const eid = e.id || e.generated_at || String(i);
              return (
                <li key={eid}>
                  <button
                    onClick={() => openEntry(eid)}
                    data-testid={`mh-list-item-${i}`}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-900/40 group"
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <span className="text-sm font-mono uppercase text-zinc-300 group-hover:text-zinc-50 truncate">
                        {fmtShort(e.generated_at)}
                      </span>
                      {e.is_incremental && (
                        <span className="text-xs font-mono uppercase text-blue-400 border border-blue-500/30 bg-blue-500/10 rounded-sm px-1 leading-4 shrink-0">
                          Δ
                        </span>
                      )}
                    </span>
                    <span className="text-xs font-mono uppercase text-zinc-600 shrink-0">
                      {(e.citations?.length || 0)} src
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        </>
        )}
      </div>

      {/* Popup dialog focused on the clicked entry, with the rest of the timeline below */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setFocusEntryId(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto rounded-sm" data-testid="market-history-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2 text-base font-mono uppercase tracking-wider text-zinc-200">
              <Sparkles className="w-4 h-4 text-blue-400" />
              Market History · <span className="text-blue-400">{trade?.ticker}</span>
              {hasAny && <span className="text-xs text-zinc-500">({entries.length} update{entries.length === 1 ? "" : "s"})</span>}
            </DialogTitle>
          </DialogHeader>

          {isError && (
            <div className="text-sm text-rose-400 py-2">
              Analysis failed. {trade?.market_history_error ? <span className="text-zinc-500">({trade.market_history_error})</span> : null}
            </div>
          )}

          {hasAny ? (
            <div className="space-y-5">
              {orderedEntries.map((e, i) => (
                <EntryRow key={e.id || e.generated_at || i} entry={e} index={i} defaultOpen={i === 0} />
              ))}

              <div className="flex items-center justify-between pt-2 border-t border-zinc-900">
                <span className="text-[10px] font-mono uppercase text-zinc-600">
                  {entries.length} historical update{entries.length === 1 ? "" : "s"} preserved
                </span>
                <button
                  onClick={regenerate}
                  disabled={busy || isPending}
                  data-testid="mh-regenerate"
                  className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-zinc-300 hover:text-zinc-100 disabled:opacity-40"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${busy || isPending ? "animate-spin" : ""}`} /> Regenerate
                </button>
              </div>
            </div>
          ) : (
            !isError && <div className="text-sm text-zinc-500 py-3">Nothing to show yet.</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
